import type { MouseEvent, ReactNode } from "react"
import { Link } from "react-router"
import { cn } from "@/primitives/utils"

/*
 * The compact layout's tab bar, along the bottom of the screen within a thumb's reach: a row of
 * tabs, each an icon over its name, the current one in the foreground and the rest muted. Colour
 * is not the current tab's only mark: its name is semibold and its icon's line heavier. Nav is
 * never teal, here as in the sidebar. It sits on the canvas with a hairline above it, and keeps
 * clear of the home indicator and a landscape phone's corners.
 */

/** The bar itself, named for what it navigates, holding its `TabBarTab`s in order. */
export function TabBar({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <nav
      aria-label={label}
      className={cn(
        "shrink-0 border-t border-border-subtle bg-sidebar px-safe pb-safe text-sidebar-muted-foreground",
        className,
      )}
    >
      <ul className="mx-auto flex h-tab-bar max-w-lg">{children}</ul>
    </nav>
  )
}

/** A count no wider than the tab can hold: up to 99, then "99+". */
const countShown = (count: number) => (count > 99 ? "99+" : String(count))

/**
 * One tab: a link to its page (`to`), or a button for what opens over the page (Search, the
 * avatar's drawer). `icon` is a 20px glyph, or a disc. `count`, above zero, is a small figure on
 * the icon's shoulder, read after the name. `dot` is something waiting behind the tab ("Update
 * ready" on the avatar), drawn as a dot on the icon's shoulder and read after the name too.
 * `onSelect` hears the tap first, and may keep a link from following (the current tab returns its
 * page to the top instead).
 */
export function TabBarTab({
  label,
  icon,
  current = false,
  count,
  dot,
  to,
  onSelect,
}: {
  label: string
  icon: ReactNode
  current?: boolean
  count?: number
  dot?: string
  to?: string
  onSelect?: (event: MouseEvent<HTMLElement>) => void
}) {
  const look = cn(
    "flex size-full flex-col items-center justify-center gap-1 rounded-lg outline-none select-none hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-inset",
    current && "text-sidebar-foreground",
  )
  const shown = count !== undefined && count > 0
  // The count and the dot are read after the name as one phrase ("My tickets, 5"; "You, Update
  // ready"): a hidden span beside the name would be read as a separate word, with a space before
  // its comma.
  const said = [shown ? String(count) : undefined, dot].filter(Boolean)
  const name = said.length > 0 ? [label, ...said].join(", ") : undefined
  const content = (
    <>
      <span
        className={cn(
          "relative flex size-6 items-center justify-center [&>svg]:size-5",
          current && "[&>svg]:stroke-emphasis",
        )}
      >
        {icon}
        {shown && (
          <span
            aria-hidden
            className="absolute -top-1 left-4 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-2xs font-semibold text-background tabular-nums ring-2 ring-sidebar"
          >
            {countShown(count)}
          </span>
        )}
        {dot && !shown && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-foreground ring-2 ring-sidebar"
          />
        )}
      </span>
      <span className={cn("text-2xs", current ? "font-semibold" : "font-medium")}>{label}</span>
    </>
  )
  return (
    <li className="flex min-w-0 flex-1">
      {to === undefined ? (
        <button type="button" aria-label={name} onClick={onSelect} className={look}>
          {content}
        </button>
      ) : (
        <Link
          to={to}
          aria-current={current ? "page" : undefined}
          aria-label={name}
          onClick={onSelect}
          className={look}
        >
          {content}
        </Link>
      )}
    </li>
  )
}
